import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
	useDeletePersonalPlayListMutation,
	useDeleteStudioPlayListMutation,
	usePersonalPlayListMutation,
	useSubmitPersonalPlayListMutation
} from '../../../../service';
import { getFeaturedPlayList } from '../../../../redux/featuredPlayListSlice';
import { getPersonalPlayList } from '../../../../redux/personalPlayListSlice';
import { ContentPasteSearchOutlined } from '@mui/icons-material';

export const usePlaylistActions = ({
	userInfo,
	setModal,
	setModalShow,
	setEditPlaylistID,
	setPlayListId,
	setPlaylistmodal,
	setUpdatePlayList,
	setIsReordering
}) => {
	const dispatch = useDispatch();

	// Safely extract userId to avoid null reference errors
	const userId = useMemo(() => userInfo?.userId || null, [userInfo]);

	// Mutations
	const [reqPersonalPlayList, resPersonalPlayList] = usePersonalPlayListMutation();
	const [reqDelete, resDelete] = useDeletePersonalPlayListMutation();
	const [reqStudioDelete, resStudioDelete] = useDeleteStudioPlayListMutation();
	const [reqPersonalDelete, resPersonalDelete] = useSubmitPersonalPlayListMutation();

	const refreshPlaylists = useCallback(() => {
		if (userId) {
			reqPersonalPlayList({ userId });
		}
	}, [reqPersonalPlayList, userId]);

	// Show success toast on successful deletion
	useEffect(() => {
		if (resDelete?.isSuccess || resStudioDelete?.isSuccess) {
			refreshPlaylists();
			toast.success('Deleted Successfully');
		}
	}, [resDelete?.isSuccess, resStudioDelete?.isSuccess, refreshPlaylists]);

	useEffect(() => {
		if (resPersonalPlayList?.isSuccess && resPersonalPlayList?.data?.existingVideos) {
			dispatch(getPersonalPlayList(resPersonalPlayList?.data?.existingVideos));
		} else if (resPersonalPlayList?.isSuccess) {
			dispatch(getPersonalPlayList([]));
		}
	}, [resPersonalPlayList, dispatch]);

	// Store featured playlist data in Redux when fetched successfully
	const initializePlaylistData = (resFeaturedPlayList, nshowModal) => {
		if (resFeaturedPlayList?.isSuccess && resFeaturedPlayList?.data && !userInfo) {
			const { existingVideos } = resFeaturedPlayList.data;
			dispatch(getFeaturedPlayList(existingVideos));
		}

		if (userId) {
			reqPersonalPlayList({ userId });
		}
	};

	// Handle playlist editing
	const handleEditPlaylist = (e, playlist) => {
		e.preventDefault();
		setModalShow(true);
		setIsReordering(false)
		setEditPlaylistID(playlist?._id);
	};

	// Handle playlist deletion
	const handleDeletePlaylist = (e, playlist) => {
		
		if (e && e.preventDefault) {
			e.preventDefault();
			setPlayListId(playlist?._id);
			setPlaylistmodal(true);
		} else {
			// Direct deletion with ID and type
			const playlistId = e;
			const playlistType = playlist;
			if (playlistType === 'SPL') {
				reqStudioDelete(playlistId);
			} else {
				reqDelete(playlistId);
			}
		}
	};

	// Handle video deletion from playlist
	const handleDeleteVideo = (e, playlist, video) => {
		e.preventDefault();
		const updatedVideos = playlist?.videos?.filter(v => v?._id !== video?._id) || [];
		const updatedPlaylist = {
			...playlist,
			videos: updatedVideos
		};
		setUpdatePlayList(updatedPlaylist);
		setModal(true);
	};

	// Handle playlist update
	const handleUpdatePlaylist = playlist => {
		if (!userId) return;
		reqPersonalDelete({
			createdAt: playlist?.createdAt,
			description: playlist?.description,
			name: playlist?.name,
			videos: playlist?.videos,
			id: playlist?._id,
			userId
		});
	};

	return {
		handleEditPlaylist,
		handleDeletePlaylist,
		handleDeleteVideo,
		handleUpdatePlaylist,
		initializePlaylistData,
		refreshPlaylists,
		reqPersonalPlayList,
		resPersonalPlayList,
		resDelete,
		resStudioDelete,
		resPersonalDelete
	};
};
